import { ReactNode } from 'react';

import {
   VisitPatientConsentDocumentApprovalResult,
   VisitPatientConsentDocumentApproveCommand,
} from '@dreryk/egabinet-visit-frontend-api';

import { useCqrsCommand } from '@dreryk/command-query-react-common';
import { ActionEventInterceptor } from '@dreryk/react-components';
import { useDraftRemoveByTokenCqrsCommand } from '@dreryk/react-draft';
import { Choice } from '@dreryk/watermelon';

import {
   DocumentTemplateFormBean,
   DocumentTemplateFormController,
   documentTemplateValuesMapToVisitDocumentFieldValue,
} from '~libs.desktop.shared.document-template';

import { getConsentTemplateDraftToken } from '../../consentTemplate.draft';
import { translationKeys } from '../../translations';

import { VisitConsentApproveOnSuccessCallback } from './VisitConsentApproveActionInterceptor.types';
import { getConsentTemplateToken } from './VisitConsentApproveActionInterceptor.utils';

type VisitConsentApproveActionInterceptorProps = {
   readonly children: ReactNode;
   readonly patientId: number;
   readonly visitId: number;
   readonly printTemplateId: number;
   readonly onSuccess: VisitConsentApproveOnSuccessCallback;
} & Pick<DocumentTemplateFormController, 'handleSubmit' | 'markPristine'>;

export const VisitConsentApproveActionInterceptor = (props: VisitConsentApproveActionInterceptorProps): JSX.Element => {
   const { executeAsync: removeDraft } = useDraftRemoveByTokenCqrsCommand();

   const { executeAsync: approveVisitDocument } = useCqrsCommand<
      VisitPatientConsentDocumentApprovalResult,
      DocumentTemplateFormBean
   >(
      (parameters) => {
         const command = new VisitPatientConsentDocumentApproveCommand();
         command.fieldValues = documentTemplateValuesMapToVisitDocumentFieldValue(parameters.documentTemplateValues);
         command.token = Choice.of(getConsentTemplateToken(props.patientId, props.printTemplateId));
         command.printTemplateId = props.printTemplateId;
         command.visitId = props.visitId;
         return command;
      },
      {
         onSuccess: async (result) => {
            props.markPristine();
            await props.onSuccess?.(result);
            await removeDraft({ token: getConsentTemplateDraftToken(props.visitId, props.printTemplateId) });
         },
      }
   );

   return (
      <ActionEventInterceptor
         action={props.handleSubmit(approveVisitDocument)}
         blockingOperation={translationKeys.confirmingDocument}
      >
         {props.children}
      </ActionEventInterceptor>
   );
};
